import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

import FormField from "@mhj/shared/FormField";
import { apiRequest } from "@mhj/shared/api";
import type { ProjectItem } from "@mhj/shared/types";

type ProjectForm = {
  title: string;
  shortCaption: string;
  date: string;
  location: string;
  content: string;
};

export default function AdminProjects() {
  const queryClient = useQueryClient();
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [selected, setSelected] = useState<ProjectItem | null>(null);
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);
  const [rawMarkdown, setRawMarkdown] = useState("");
  const [showRawEditor, setShowRawEditor] = useState(false);
  const [rawLoadedSlug, setRawLoadedSlug] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ProjectForm>({
    defaultValues: {
      title: "",
      shortCaption: "",
      date: "",
      location: "",
      content: "",
    },
  });
  const { data, error } = useQuery({
    queryKey: ["admin", "projects"],
    queryFn: () => apiRequest<{ projects: ProjectItem[] }>("/projects"),
  });

  const saveMutation = useMutation({
    mutationFn: (payload: Omit<ProjectItem, "slug">) =>
      apiRequest(
        selected ? `/admin/projects/${selected.slug}` : "/admin/projects",
        {
          method: selected ? "PUT" : "POST",
          data: payload,
          withCsrf: true,
        }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "projects"] });
      resetForm();
      toast.success(selected ? "Project updated." : "Project created.");
    },
  });

  const uploadImageMutation = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiRequest<{ url: string }>("/admin/projects/upload-image", {
        method: "POST",
        data: formData,
        withCsrf: true,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (slug: string) =>
      apiRequest(`/admin/projects/${slug}`, {
        method: "DELETE",
        withCsrf: true,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "projects"] });
      if (selected?.slug === deletingSlug.current) {
        resetForm();
      }
      toast.success("Project deleted.");
    },
  });

  const deletingSlug = React.useRef<string | null>(null);

  const loadRawMutation = useMutation({
    mutationFn: (slug: string) =>
      apiRequest<{ markdown: string }>(`/admin/projects/${slug}/raw`),
    onSuccess: (response) => {
      setRawMarkdown(response.markdown);
      setRawLoadedSlug(selected?.slug ?? null);
      setShowRawEditor(true);
    },
  });

  const saveRawMutation = useMutation({
    mutationFn: (payload: { slug: string; markdown: string }) =>
      apiRequest(`/admin/projects/${payload.slug}/raw`, {
        method: "PUT",
        data: { markdown: payload.markdown },
        withCsrf: true,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "projects"] });
      toast.success("Project markdown updated.");
    },
  });

  useEffect(() => {
    if (data?.projects) {
      setProjects(data.projects);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      toast.error((error as Error).message);
    }
  }, [error]);

  const resetForm = () => {
    setSelected(null);
    reset({
      title: "",
      shortCaption: "",
      date: "",
      location: "",
      content: "",
    });
    setGalleryUrls([]);
    setShowRawEditor(false);
    setRawMarkdown("");
    setRawLoadedSlug(null);
  };

  const handleSave = async (values: ProjectForm) => {
    if (galleryUrls.length === 0) {
      toast.error("Upload at least one project image.");
      return;
    }
    try {
      const payload = {
        title: values.title,
        shortCaption: values.shortCaption,
        date: values.date,
        location: values.location,
        gallery: galleryUrls,
        content: values.content,
      };
      await saveMutation.mutateAsync(payload);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleEdit = (project: ProjectItem) => {
    setSelected(project);
    reset({
      title: project.title,
      shortCaption: project.shortCaption,
      date: project.date,
      location: project.location,
      content: project.content,
    });
    setGalleryUrls(project.gallery);
    setShowRawEditor(false);
    setRawMarkdown("");
    setRawLoadedSlug(null);
  };

  const handleLoadRawMarkdown = async () => {
    if (!selected) return;
    try {
      await loadRawMutation.mutateAsync(selected.slug);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleSaveRawMarkdown = async () => {
    if (!selected) return;
    if (rawMarkdown.trim().length < 3) {
      toast.error("Markdown cannot be empty.");
      return;
    }
    try {
      await saveRawMutation.mutateAsync({
        slug: selected.slug,
        markdown: rawMarkdown,
      });
      setRawLoadedSlug(selected.slug);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleGalleryUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    try {
      const uploadedUrls: string[] = [];
      for (const file of files) {
        const response = await uploadImageMutation.mutateAsync(file);
        uploadedUrls.push(response.url);
      }
      setGalleryUrls((prev) => [...prev, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} image${uploadedUrls.length > 1 ? "s" : ""} uploaded.`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      event.target.value = "";
    }
  };

  const handleRemoveGalleryImage = (url: string) => {
    setGalleryUrls((prev) => prev.filter((item) => item !== url));
  };

  const handleDelete = async (project: ProjectItem) => {
    deletingSlug.current = project.slug;
    try {
      await deleteMutation.mutateAsync(project.slug);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const [title, shortCaption, date, location, content] = watch([
    "title",
    "shortCaption",
    "date",
    "location",
    "content",
  ]);

  const markdownPreview = useMemo(() => {
    const galleryLines = galleryUrls
      .map((item) => `  - ${item}`)
      .join("\n");

    return `---\n` +
      `title: "${title || "Project Title"}"\n` +
      `shortCaption: "${shortCaption || "Short caption"}"\n` +
      `date: "${date || "2026-01-01"}"\n` +
      `location: "${location || "Minna, Niger State"}"\n` +
      `gallery:\n${galleryLines || "  - https://..."}\n` +
      `---\n\n` +
      `${content || "Full markdown description here..."}`;
  }, [title, shortCaption, date, location, galleryUrls, content]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-primary-500">
          Projects manager
        </p>
        <h2 className="mt-3 text-2xl font-semibold text-black">
          {selected ? "Edit project" : "Create new project"}
        </h2>
        <form onSubmit={handleSubmit(handleSave)} className="mt-6 space-y-4">
          <FormField label="Title">
            <input
              type="text"
              {...register("title", { required: "Title is required" })}
              className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-primary-500 focus:outline-none"
            />
          </FormField>
          {errors.title ? (
            <p className="text-xs text-red-200">{errors.title.message}</p>
          ) : null}
          <FormField label="Short caption">
            <input
              type="text"
              {...register("shortCaption", { required: "Caption is required" })}
              className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-primary-500 focus:outline-none"
            />
          </FormField>
          {errors.shortCaption ? (
            <p className="text-xs text-red-200">{errors.shortCaption.message}</p>
          ) : null}
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="Date">
              <input
                type="date"
                {...register("date", { required: "Date is required" })}
                className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-primary-500 focus:outline-none"
              />
            </FormField>
            <FormField label="Location">
              <input
                type="text"
                {...register("location", { required: "Location is required" })}
                className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-primary-500 focus:outline-none"
              />
            </FormField>
          </div>
          {errors.date || errors.location ? (
            <p className="text-xs text-red-200">Date and location are required.</p>
          ) : null}
          <FormField label="Project images">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={(event) => void handleGalleryUpload(event)}
              className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-primary-500 focus:outline-none"
            />
          </FormField>
          <p className="text-xs text-black/50">
            Upload JPG, PNG, or WEBP images (max 5MB each).
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {galleryUrls.map((url) => (
              <div
                key={url}
                className="rounded-2xl border border-black/10 bg-white/70 p-3"
              >
                <img
                  src={url}
                  alt="Project upload preview"
                  className="h-28 w-full rounded-xl object-cover"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveGalleryImage(url)}
                  className="mt-3 rounded-full border border-red-500/40 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-red-500 transition hover:border-red-400"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <FormField label="Markdown content">
            <textarea
              {...register("content", { required: "Content is required" })}
              rows={6}
              className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black focus:border-primary-500 focus:outline-none"
            />
          </FormField>
          {errors.content ? (
            <p className="text-xs text-red-200">{errors.content.message}</p>
          ) : null}
          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={saveMutation.isPending || uploadImageMutation.isPending}
              className="rounded-full bg-blue-500 px-6 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-blue-400 disabled:cursor-not-allowed disabled:bg-blue-200"
            >
              {saveMutation.isPending || uploadImageMutation.isPending
                ? "Saving..."
                : selected
                  ? "Update project"
                  : "Create project"}
            </button>
            {selected ? (
              <button
                type="button"
                onClick={resetForm}
                className="rounded-full border border-white/20 px-6 py-3 text-xs uppercase tracking-[0.3em] text-white/70 transition hover:border-primary-500 hover:text-primary-200"
              >
                Cancel edit
              </button>
            ) : null}
            {selected ? (
              <button
                type="button"
                onClick={() => void handleLoadRawMarkdown()}
                disabled={loadRawMutation.isPending}
                className="rounded-full border border-black/20 px-6 py-3 text-xs uppercase tracking-[0.3em] text-black/70 transition hover:border-primary-500 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loadRawMutation.isPending ? "Loading .md..." : "Edit .md file"}
              </button>
            ) : null}
          </div>
        </form>
        {selected && showRawEditor ? (
          <div className="mt-8 rounded-2xl border border-black/10 bg-white/70 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-[0.2em] text-black/60">
                Raw markdown editor ({selected.slug}.md)
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowRawEditor(false)}
                  className="rounded-full border border-black/20 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-black/60 transition hover:border-black/40"
                >
                  Hide
                </button>
                <button
                  type="button"
                  onClick={() => void handleSaveRawMarkdown()}
                  disabled={saveRawMutation.isPending}
                  className="rounded-full bg-blue-500 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-white transition hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saveRawMutation.isPending ? "Saving..." : "Save .md"}
                </button>
              </div>
            </div>
            <textarea
              value={rawMarkdown}
              onChange={(event) => setRawMarkdown(event.target.value)}
              rows={14}
              className="mt-4 w-full rounded-2xl border border-black/15 bg-white/80 px-4 py-3 font-mono text-xs text-black focus:border-primary-500 focus:outline-none"
            />
            <p className="mt-2 text-[11px] text-black/50">
              Include full frontmatter and markdown body. Last loaded:
              {" "}
              {rawLoadedSlug ?? "not loaded"}
            </p>
          </div>
        ) : null}
      </div>
      <div className="space-y-6">
        <div className="glass-card rounded-3xl p-6">
          <p className="text-xs uppercase tracking-[0.3em] text-primary-500">
            Markdown preview
          </p>
          <pre className="mt-4 max-h-[360px] overflow-auto rounded-2xl border border-white/10 bg-black/70 p-4 text-xs text-white/70">
            {markdownPreview}
          </pre>
        </div>
        <div className="glass-card rounded-3xl p-6">
          <p className="text-xs uppercase tracking-[0.3em] text-primary-500">
            Existing projects
          </p>
          <div className="mt-4 space-y-3 text-sm text-black/70">
            {projects.length === 0 ? (
              <p>No projects yet.</p>
            ) : (
              projects.map((project) => (
                <div
                  key={project.slug}
                  className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3"
                >
                  <p className="font-semibold text-black">{project.title}</p>
                  <p className="text-xs text-black/50">{project.location}</p>
                  <div className="mt-3 flex gap-3 text-xs">
                    <button
                      onClick={() => handleEdit(project)}
                      className="rounded-full border border-white/20 px-3 py-1 text-xs uppercase tracking-[0.2em] text-white/70 transition hover:border-primary-500 hover:text-primary-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => void handleDelete(project)}
                      className="rounded-full border border-red-500/40 px-3 py-1 text-xs uppercase tracking-[0.2em] text-red-200 transition hover:border-red-400 hover:text-red-100"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
